<template>
  <div>
    <div v-if="!loadingIncident">
      <page-header>
        <template #title>
          <h1 class="page-title">
            Closed Claim - {{ claimReference }}
            <VulnerabilityLabel v-if="hasVulnerabilities"></VulnerabilityLabel>
          </h1>
        </template>
        <template #actions>
          <page-header-action @click.prevent="toggleNotes">
            Notes
          </page-header-action>
          <page-header-action
            tag="form-button"
            :indicator="indicator"
            @clicked="openClaim"
            v-if="!readonly"
          >
            Re-Open Claim
          </page-header-action>
        </template>
      </page-header>
      <div class="row">
        <div class="col-lg-12">
          <div class="card mb-3">
            <incident-tabs
              :tabs="tabs"
              :source="source"
              :incident="incident"
              :module="module"
              v-on:selected="setActive"
              :active="currentView"
              :email="false"
            ></incident-tabs>

            <transition-group name="component-fade" mode="out-in">
              <recovery
                :incident="incident"
                :module="module"
                :recovery="recovery"
                :readonly="readonly"
                v-show="currentView == 'recovery'"
                key="recovery"
                v-if="!!recovery"
              ></recovery>

              <claim-monitoring
                :incident="incident"
                :module="module"
                :readonly="readonly"
                v-show="currentView == 'claim_monitoring'"
                key="claim_monitoring"
              ></claim-monitoring>

              <incident-source
                :incident="incident"
                :showCancel="showCancel"
                :readonly="readonly"
                v-on:created="activateTabs"
                v-on:exists="activateTabs"
                v-if="currentView == 'source'"
                v-on:unsaved-data="saveStatus"
                key="source"
              ></incident-source>

              <client
                :incident="incident"
                :module="module"
                :readonly="readonly"
                v-if="currentView == 'client'"
                v-on:unsaved-data="saveStatus"
                key="client"
              ></client>

              <accident-details
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'accident_description'"
                v-on:unsaved-data="saveStatus"
                key="accidentDetails"
              ></accident-details>

              <third-parties
                :incident="incident"
                :module="module"
                :readonly="readonly"
                v-if="currentView == 'third_parties'"
                v-on:unsaved-data="saveStatus"
                key="third_parties"
              ></third-parties>

              <witnesses
                :incident="incident"
                :readonly="readonly"
                key="witnesses"
                v-if="currentView == 'witnesses'"
              ></witnesses>

              <statements
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'statements'"
                key="statements"
                v-on:unsaved-data="saveStatus"
              ></statements>

              <documents
                :incident="incident"
                :readonly="readonly"
                key="documents"
                v-if="currentView == 'documents'"
              ></documents>

              <jobs
                :incident="source.incident_id"
                v-if="currentView == 'jobs'"
                key="jobs"
              ></jobs>
              <email :incident="incident" v-if="currentView == 'email'"></email>
            </transition-group>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>

    <notes-window
      :show="notes"
      :incident="incident"
      :create="false"
      v-on:toggle-note="toggleNotes"
    ></notes-window>
  </div>
</template>

<script>
import Source from '../FirstNoticeOfLoss/Sections/Source.vue';
import AccidentDetails from '../FirstNoticeOfLoss/Sections/AccidentDetails.vue';
import Client from '../FirstNoticeOfLoss/Sections/Client.vue';
import ThirdParties from '../FirstNoticeOfLoss/Sections/ThirdParties.vue';
import Witnesses from '../FirstNoticeOfLoss/Sections/Witnesses.vue';
import Statements from '../FirstNoticeOfLoss/Sections/Statements.vue';
import Notes from './../Shared/Notes.vue';
import Documents from './../Shared/Documents.vue';
import Email from './../Shared/Email.vue';
import IncidentTabs from './../Shared/IncidentTabs';
import Recovery from '@/components/Modules/Recovery/Sections/Recovery';
import Jobs from './../FirstNoticeOfLoss/Sections/Jobs';
import ClaimMonitoring from '../HireMonitoring/Sections/ClaimMonitoring.vue';
import VulnerabilityLabel from '@/components/Modules/Shared/VulnerabilityLabel.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    }
  },

  components: {
    VulnerabilityLabel,
    PageHeader,
    PageHeaderAction,
    'incident-source': Source,
    AccidentDetails,
    Client,
    ThirdParties,
    Witnesses,
    Statements,
    'notes-window': Notes,
    IncidentTabs,
    Documents,
    Email,
    Recovery,
    Jobs,
    'claim-monitoring': ClaimMonitoring
  },

  data() {
    return {
      module: 'Closed',
      notes: false,
      indicator: false,
      currentView: 'recovery',
      unsaved_data: false,
      tabs: [
        {
          name: 'recovery',
          label: 'Recovery',
          disabled: false
        },
        {
          name: 'claim_monitoring',
          label: 'Claim Monitoring'
        },
        {
          name: 'jobs',
          label: 'Jobs',
          disabled: false
        }
      ],
      showCancel: false
    };
  },

  computed: {
    readonly() {
      return this.isReadonly();
    },

    claimReference() {
      const source = this.$store.getters['Incident/source'];
      if (source && source.claim_reference) {
        return source.claim_reference;
      }
      return null;
    },

    recovery() {
      return this.$store.getters['DebtRecovery/get'];
    },

    source() {
      return this.$store.getters['Incident/source'];
    },

    loadingIncident() {
      return this.$store.getters['Incident/loading'];
    },

    hasVulnerabilities() {
      const vulnerabilities =
        this.$store.getters['Incident/vulnerabilities'] || [];

      return vulnerabilities.length > 0;
    }
  },

  mounted() {
    this.$store.dispatch('Incident/retrieve', this.incident);
    this.$store.commit('DebtRecovery/clearRecord');
    this.$store.commit('DebtRecoveryStatuses/retrieve');
    this.$store.commit('HoldBackReasons/retrieve');
    this.$store.commit('RateGroups/retrieve');
    this.$store.commit('ShortFallReasons/retrieve');

    window.Ajax.get(`/debt-recovery/${this.incident}`)
      .then(response => {
        this.$store.commit('DebtRecovery/set', response.data);
      })
      .catch(error => {});
  },

  methods: {
    setActive(tab) {
      // check if unsaved data on the current form
      if (this.unsaved_data && this.currentView !== tab) {
        this.saveWarning(tab); // show alert if there is
      } else {
        this.currentView = tab;
      }
    },

    activateTabs() {
      this.tabs.forEach(tab => {
        tab.disabled = false;
      });
      this.showCancel = false;
    },

    saveStatus(event) {
      this.unsaved_data = event;
    },

    toggleNotes() {
      this.notes = !this.notes;
    },

    saveWarning(tab) {
      window
        .swal({
          title: 'Are you sure?',
          text:
            'You will lose all details entered in this section if you continue!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue without saving',
          cancelButtonText: 'Cancel and stay in this section'
        })
        .then(
          () => {
            // Go to selected tab discarding changes
            this.currentView = tab;
            this.unsaved_data = false;
          },
          dismiss => {}
        );
    },

    openClaim() {
      if (this.readonly) return;
      window.Ajax.post(`/claim-monitoring/${this.incident}`)
        .then(response => {
          this.route(`/recovery/${this.incident}`);
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Sorry, there was an issue moving the incident to recovery.'
          );
        });
    }
  },

  beforeDestroy() {
    this.$store.commit('Incident/clearRecord');
  }
};
</script>
